/**
 * SettingsPage — tabbed settings hub.
 *
 * Tabs:
 *   General    — display name, email (read-only), timezone
 *   LinkedIn   — LinkedInConnect component
 *   ICP        — IcpSettings component
 *   Calendly   — CalendlySettings component
 *   Account    — plan info, billing, danger zone
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout/page-header'
import { SectionHelp } from '@/components/help/section-help'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ExternalLink, Loader2, CheckCircle2, Mail } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useUser, useClerk } from '@clerk/clerk-react'
import { usePlan } from '@/hooks/use-plan'
import LinkedInConnect from '@/features/settings/linkedin-connect'
import IcpSettings from '@/features/settings/icp-settings'
import OfferSettings from '@/features/settings/offer-settings'
import MessageSettings from '@/features/settings/message-settings'
import CtaSettings from '@/features/settings/cta-settings'
import ContentSettings from '@/features/settings/content-settings'
import FaqSettings from '@/features/settings/faq-settings'
import ObjectionSettings from '@/features/settings/objection-settings'
import VoiceSettings from '@/features/settings/voice-settings'
import SafetySettings from '@/features/settings/safety-settings'
import CalendlySettings from '@/features/settings/calendly-settings'
import IntegrationsSettings from '@/features/settings/integrations-settings'
import { useApi } from '@/hooks/use-api'

// ============================================================
// Static data
// ============================================================

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
] as const

// ============================================================
// General tab
// ============================================================

function GeneralTab() {
  const { user } = useUser()
  const apiFetch = useApi()
  const [displayName, setDisplayName] = useState(
    () => user?.fullName ?? [user?.firstName, user?.lastName].filter(Boolean).join(' ') ?? ''
  )
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  )
  const [saving, setSaving] = useState(false)
  const [savedSuccess, setSavedSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Load saved timezone from backend on mount
  const { data: savedPrefs } = useQuery({
    queryKey: ['settings', 'general'],
    queryFn: async () => {
      const res = await apiFetch<{ timezone?: string }>('/api/settings/general')
      return res.data ?? null
    },
    staleTime: 5 * 60_000,
  })

  // Apply saved timezone once loaded
  useState(() => {
    if (savedPrefs?.timezone) setTimezone(savedPrefs.timezone)
  })

  // Update timezone when backend data arrives
  useEffect(() => {
    if (savedPrefs?.timezone) setTimezone(savedPrefs.timezone)
  }, [savedPrefs])

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    const res = await apiFetch('/api/settings/general', {
      method: 'PUT',
      body: { timezone, displayName: displayName.trim() },
    })
    setSaving(false)
    if (!res.success) {
      setSaveError(res.error ?? 'Failed to save preferences')
      return
    }
    setSavedSuccess(true)
    setTimeout(() => setSavedSuccess(false), 3000)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Your public display name and account email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="display-name">Display Name</Label>
            <Input
              id="display-name"
              placeholder="Your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={user?.primaryEmailAddress?.emailAddress ?? ''}
              readOnly
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Managed by your authentication provider. Contact support to change.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
          <CardDescription>Timezone used for scheduling and activity reports.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="timezone" className="w-full max-w-xs">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="default" size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </Button>
            {savedSuccess && (
              <span className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Saved
              </span>
            )}
            {saveError && <span className="text-sm text-destructive">{saveError}</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ============================================================
// Account tab
// ============================================================

function AccountTab() {
  const apiFetch = useApi()
  const { user } = useUser()
  const { signOut } = useClerk()
  const effectivePlan = usePlan()
  const [portalLoading, setPortalLoading] = useState(false)
  const [portalError, setPortalError] = useState<string | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  // Account deletion state
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Prefer the effective plan (D1 + comp override) over raw Clerk metadata.
  // Clerk publicMetadata frequently lags behind D1 (Stripe webhook updates D1
  // first; Clerk sync isn't guaranteed). usePlan() handles that fallback chain.
  const currentPlan = effectivePlan
  const planLabel = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)
  const hasSubscription = currentPlan !== 'starter'
  const userEmail = user?.primaryEmailAddress?.emailAddress ?? ''

  // Manual-cancel mailto. ALWAYS available regardless of Stripe state — this
  // is the guaranteed-works escape hatch for users whose stripe_customer_id
  // is null in D1 (the V3 checkout webhook never linked it back, or they were
  // grandfathered in via a different path). Without this, those users would
  // have no way to stop billing.
  const cancelMailto = `mailto:support@knwn4.com?subject=${encodeURIComponent(
    'Cancel my DCA subscription',
  )}&body=${encodeURIComponent(
    `Hi KNWN4,\n\nPlease cancel my DealCloser Autopilot subscription.\n\nEmail: ${userEmail}\nUser ID: ${user?.id ?? 'unknown'}\n\nThanks.`,
  )}`

  async function handleManageBilling() {
    setPortalLoading(true)
    setPortalError(null)
    const res = await apiFetch<{ url: string }>('/api/billing/portal', { method: 'POST' })
    setPortalLoading(false)
    if (!res.success || !res.data) {
      // /billing/portal now returns structured error codes in res.error:
      //   comp_account        — is_comp=1, no Stripe customer to manage
      //   no_stripe_customer  — paid plan but stripe_customer_id is NULL on D1
      //                         (v2-legacy migration, webhook never linked back)
      //   stripe_api_error    — Stripe rejected the portal session create
      //   user_not_found      — Clerk JWT valid but no D1 user row yet
      // Anything else (network, raw status fallback) → show the raw error.
      const code = res.error?.toLowerCase() ?? ''
      let friendly: string
      if (code.includes('comp_account')) {
        friendly =
          'Your account is complimentary — no Stripe billing to manage. Contact support@knwn4.com if this is incorrect.'
      } else if (code.includes('no_stripe_customer') || code.includes('stripe customer')) {
        friendly =
          "No active subscription is linked here. Use \"Cancel subscription via email\" below if you need help — we'll handle it manually."
      } else if (code.includes('stripe_api_error')) {
        friendly =
          'Stripe rejected the request. Use "Cancel subscription via email" below for immediate help.'
      } else if (code.includes('user_not_found')) {
        friendly =
          'Your account is still syncing. Refresh in a moment, or use "Cancel subscription via email" below.'
      } else {
        friendly = res.error ?? 'Failed to open billing portal'
      }
      setPortalError(friendly)
      return
    }
    window.open(res.data.url, '_blank')
  }

  async function handleDeleteAccount() {
    setDeleteLoading(true)
    setDeleteError(null)
    // Cancel the Stripe subscription FIRST so billing stops. DELETE /auth/me
    // wipes stripe_subscription_id in the D1 cascade, so we'd lose the link to
    // cancel afterwards. If cancellation fails, we abort the delete rather than
    // leave the customer billed for a wiped account.
    const cancelRes = await apiFetch<{ cancelled: boolean }>('/api/billing/cancel', { method: 'POST' })
    if (!cancelRes.success) {
      setDeleteLoading(false)
      setDeleteError(
        "We couldn't cancel your subscription, so we didn't delete the account (to avoid leaving you billed). Please try again, or contact support@knwn4.com.",
      )
      return
    }
    const res = await apiFetch<{ message: string; partial_errors?: Array<{ stage: string; message: string }> }>(
      '/api/auth/me',
      { method: 'DELETE' },
    )
    setDeleteLoading(false)
    if (!res.success) {
      const code = res.error?.toLowerCase() ?? ''
      if (code.includes('d1_cascade_failed')) {
        setDeleteError(
          "We couldn't fully delete your account. Contact support@knwn4.com and we'll finish the job.",
        )
      } else {
        setDeleteError(res.error ?? 'Failed to delete account')
      }
      return
    }
    // Sign out so they can't keep using a stale Clerk session, then send
    // them to the marketing site. The Clerk DELETE on the server will
    // also invalidate the session — this is just immediate-feedback UX.
    await signOut({ redirectUrl: '/' })
  }

  async function handleSubscribe() {
    setCheckoutLoading(true)
    setCheckoutError(null)
    const res = await apiFetch<{ url: string }>('/api/billing/checkout', { method: 'POST', body: { plan: 'pro' } })
    setCheckoutLoading(false)
    if (!res.success || !res.data) {
      setCheckoutError(res.error ?? 'Failed to start checkout')
      return
    }
    window.location.href = res.data.url
  }

  return (
    <div className="space-y-6">
      {/* Plan info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Current Plan</CardTitle>
            <Badge className="bg-primary/10 text-primary border-0">{planLabel}</Badge>
          </div>
          <CardDescription>Your subscription details and billing information.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Billing cycle</span>
            <span>Monthly</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Next renewal</span>
            <span>—</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {/* Manage Billing — opens Stripe Customer Portal. Only meaningful
                for users whose checkout flow successfully linked a
                stripe_customer_id to their D1 row. */}
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => void handleManageBilling()}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Manage Billing
            </Button>
            {/* Upgrade — only when on the free/starter tier. */}
            {!hasSubscription && (
              <Button
                variant="default"
                className="gap-2"
                onClick={() => void handleSubscribe()}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Upgrade Plan
              </Button>
            )}
            {/* Cancel — ALWAYS available. Mailto fallback works regardless of
                Stripe link state, so every user has a guaranteed path off the
                product. */}
            <Button asChild variant="ghost" size="sm" className="gap-2 text-muted-foreground">
              <a href={cancelMailto}>
                <Mail className="h-4 w-4" />
                Cancel subscription via email
              </a>
            </Button>
          </div>
          {portalError && <p className="text-sm text-destructive">{portalError}</p>}
          {checkoutError && <p className="text-sm text-destructive">{checkoutError}</p>}
          <p className="text-xs text-muted-foreground pt-1">
            Want to cancel right now? Click "Cancel subscription via email" — we'll stop billing within one business day, no questions asked.
          </p>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>Irreversible actions. Proceed with caution.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border border-destructive/30 p-4">
            <div>
              <p className="text-sm font-medium">Delete Account</p>
              <p className="text-xs text-muted-foreground">
                Permanently delete your account and all associated data.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setDeleteOpen(true)
                setDeleteConfirm('')
                setDeleteError(null)
              }}
            >
              Delete Account
            </Button>
          </div>
          {deleteError && (
            <p className="text-xs text-destructive mt-2">{deleteError}</p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Trouble deleting? Email{' '}
            <a href="mailto:support@knwn4.com" className="underline">support@knwn4.com</a>{' '}
            and we'll handle it manually.
          </p>
        </CardContent>
      </Card>

      {/* Delete-account confirmation dialog — requires typing the exact
          email to enable the destructive button, matches the GitHub /
          Stripe pattern. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete account?</DialogTitle>
            <DialogDescription>
              This permanently removes your DealCloser Autopilot account and
              every campaign, lead, and message tied to it. Your connected
              LinkedIn account is disconnected automatically. There is no
              undo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm">
              Type your email <span className="font-mono">{userEmail}</span> to confirm.
            </p>
            <input
              type="email"
              autoComplete="off"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder={userEmail}
            />
            {deleteError && (
              <p className="text-xs text-destructive">{deleteError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteLoading || deleteConfirm.trim().toLowerCase() !== userEmail.trim().toLowerCase()}
              onClick={() => void handleDeleteAccount()}
            >
              {deleteLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete my account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ============================================================
// Page
// ============================================================

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'general'

  function handleTabChange(value: string) {
    setSearchParams({ tab: value }, { replace: true })
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Settings"
        subtitle="Configure your account and automation preferences."
        actions={<SectionHelp sectionKey="settings" />}
      />

      <Tabs defaultValue="general" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-6 flex-wrap" data-tour="settings-tabs">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
          <TabsTrigger value="icp">ICP Targeting</TabsTrigger>
          <TabsTrigger value="outreach">Offer</TabsTrigger>
          <TabsTrigger value="voice-content">Voice & Content</TabsTrigger>
          <TabsTrigger value="safety">Safety</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="calendly">Calendly</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <GeneralTab />
        </TabsContent>

        <TabsContent value="linkedin">
          <LinkedInConnect />
        </TabsContent>

        <TabsContent value="icp">
          <IcpSettings />
        </TabsContent>

        <TabsContent value="outreach">
          <div className="space-y-8">
            <OfferSettings />
            <MessageSettings />
            <CtaSettings />
            <FaqSettings />
            <ObjectionSettings />
          </div>
        </TabsContent>

        <TabsContent value="voice-content">
          <div className="space-y-8">
            <VoiceSettings />
            <ContentSettings />
          </div>
        </TabsContent>

        <TabsContent value="safety">
          <SafetySettings />
        </TabsContent>

        <TabsContent value="integrations">
          <IntegrationsSettings />
        </TabsContent>

        <TabsContent value="calendly">
          <CalendlySettings />
        </TabsContent>

        <TabsContent value="account">
          <AccountTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
